// Status taxonomy for the Lanes module. Single source of truth — every
// reference to a status slug, its family, its label key, or its CSS
// custom-property prefix flows through here so we don't drift between
// JSX, CSS, and i18n.

export const STATUS_SLUGS = [
    "lane-empty",
    "greens",
    "oranges",
    "pinks",
    "greys",
    "greens-outbound",
    "oranges-outbound",
    "apc",
    "auction-pallets-outbound",
    "ready-for-auction",
    "auctions-filling",
    "on-auction",
    "auctions-ended",
    "unprocessed-stock",
] as const;

export type StatusSlug = typeof STATUS_SLUGS[number];

export type StatusFamily = "idle" | "stock" | "outbound" | "auction" | "attention";

const FAMILY_BY_SLUG: Record<StatusSlug, StatusFamily> = {
    "lane-empty": "idle",
    "greens": "stock",
    "oranges": "stock",
    "pinks": "stock",
    "greys": "stock",
    "greens-outbound": "outbound",
    "oranges-outbound": "outbound",
    "apc": "outbound",
    "auction-pallets-outbound": "outbound",
    "ready-for-auction": "auction",
    "auctions-filling": "auction",
    "on-auction": "auction",
    "auctions-ended": "auction",
    "unprocessed-stock": "attention",
};

export const statusFamily = (slug: StatusSlug): StatusFamily => FAMILY_BY_SLUG[slug];

// Maps each slug to the i18n key that holds its display label. Keys live
// in src/i18n/en.ts and pl.ts; the parity test guarantees both files
// stay in sync.
export const STATUS_LABEL_KEYS: Record<StatusSlug, string> = {
    "lane-empty": "lanes_status_lane_empty",
    "greens": "lanes_status_greens",
    "oranges": "lanes_status_oranges",
    "pinks": "lanes_status_pinks",
    "greys": "lanes_status_greys",
    "greens-outbound": "lanes_status_greens_outbound",
    "oranges-outbound": "lanes_status_oranges_outbound",
    "apc": "lanes_status_apc",
    "auction-pallets-outbound": "lanes_status_auction_pallets_outbound",
    "ready-for-auction": "lanes_status_ready_for_auction",
    "auctions-filling": "lanes_status_auctions_filling",
    "on-auction": "lanes_status_on_auction",
    "auctions-ended": "lanes_status_auctions_ended",
    "unprocessed-stock": "lanes_status_unprocessed_stock",
};

// Token prefix used on the four CSS custom properties for this status:
//   --status-{prefix}-bg / -fg / -border / -bar
// Identical to the slug today — kept as its own function so the rename
// is one-line if the taxonomy ever splits.
export const statusTokenPrefix = (slug: StatusSlug): string => slug;

export const isOutbound = (slug: StatusSlug): boolean => statusFamily(slug) === "outbound";

// ---- KPI category (admin-overridable) ----
//
// The category decides which summary tile on the Lanes page a status
// counts toward (and the busy/idle/unprocessed tally in zone grouping).
// It used to be derived 1:1 from `family`; it's now an editable
// per-status attribute (see laneStatusConfig.ts) so admin-created
// statuses can be slotted into the right tile. These are the built-in
// defaults the override store layers on top of. Kept in sync with the
// CATEGORIES set in jli-portal-be/.../laneStatuses.controller.js.
export type StatusCategory = "auction" | "outbound" | "stock" | "idle" | "unprocessed" | "none";

const CATEGORY_BY_FAMILY: Record<StatusFamily, StatusCategory> = {
    idle: "idle",
    stock: "stock",
    outbound: "outbound",
    auction: "auction",
    attention: "unprocessed",
};

export const STATUS_DEFAULT_CATEGORY: Record<StatusSlug, StatusCategory> = STATUS_SLUGS.reduce(
    (acc, slug) => { acc[slug] = CATEGORY_BY_FAMILY[statusFamily(slug)]; return acc; },
    {} as Record<StatusSlug, StatusCategory>,
);

// ---- applicability flags (admin-overridable) ----
//
// Whether a lane in this status carries totes, auctions, and an auction
// end date. Defaults mirror the behaviour previously hardcoded in
// mockLaneData.ts: idle lanes carry nothing; the auction end date is
// only meaningful once a lane is On Auction or Auctions Ended.
export interface StatusFlags {
    totes: boolean;
    auctions: boolean;
    auctionEndDate: boolean;
}

export const STATUS_DEFAULT_FLAGS: Record<StatusSlug, StatusFlags> = STATUS_SLUGS.reduce(
    (acc, slug) => {
        const fam = statusFamily(slug);
        acc[slug] = {
            totes: fam !== "idle",
            auctions: fam === "auction" || fam === "outbound",
            auctionEndDate: slug === "on-auction" || slug === "auctions-ended",
        };
        return acc;
    },
    {} as Record<StatusSlug, StatusFlags>,
);
